
































































import { SegmentInstanceDetails } from '@/areas/claims/claims-models';
import OtInviteUserDialog from '@/areas/invitations/ot-invite-user-dialog.vue';
import { ContractDetails } from '@/areas/projects/contracts/contract-models';
import { ProjectDetails } from '@/areas/projects/project-models';
import { Organisation } from '@/areas/settings/models/organisation-models';
import { CreateDocumentAssociationsDialogParams } from '@/components/documents/create-document-associations/create-document-associations-models';
import { DocumentDetails, DocumentVersion } from '@/components/documents/document-models';
import OtStandardHeaderArchetype from '@/components/global/archetypes/ot-standard-header-archetype.vue';
import { IInputAction, IInputTab } from '@/components/global/common-models';
import OtButton from '@/components/global/ot-button.vue';
import OtLoadingSpinner from '@/components/global/ot-loading-spinner.vue';
import OtTabs from '@/components/global/ot-tabs.vue';
import OtTag from '@/components/global/ot-tag.vue';
import OtApi, { executeApi } from '@/services/api.service';
import { vxm } from '@/store';
import { formatDateAndTime } from '@/utils/date-utils';
import { areSame } from '@/utils/string-utils';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

type GidAndName = {
  gid: string;
  name: string;
};

@Component({
  components: {
    OtStandardHeaderArchetype,
    OtTabs,
    OtLoadingSpinner,
    OtButton,
    OtTag,
    OtInviteUserDialog,
  },
})
export default class OtDocumentLayout extends Vue {
  // * PROPS
  @Prop({ required: true }) detailsRouteName!: string;
  @Prop({ required: true }) associationsRouteName!: string;
  @Prop({ required: true }) locationDescription!: string;

  // * REFS

  // * DATA
  private api = new OtApi();
  private loading = false;
  /** The effective organisation gid, based on the route params and the loaded objects */
  private effectiveOrganisationGid: string | null = null;
  private document: DocumentDetails | null = null;
  private organisation: GidAndName | null = null;
  private projectDetails: GidAndName | null = null;
  private contractDetails: GidAndName | null = null;
  private claimDetails: GidAndName | null = null;
  private isDropDownOpen = false;

  private newAssociationButton: IInputAction = {
    actionName: 'New Association',
    action: () => this.createNewDocumentAssociation(),
  };

  private get documentTabs(): IInputTab[] {
    const query = this.urlVersion ? { version: this.urlVersion } : undefined;
    const params: Required<IInputTab>['tabRoute']['params'] = { documentGid: this.documentGid };
    if (this.orgId) {
      params.orgId = this.orgId;
    }
    if (this.projectGid) {
      params.projectGid = this.projectGid;
    }
    if (this.contractGid) {
      params.contractGid = this.contractGid;
    }
    if (this.workflowGid) {
      params.workflowGid = this.workflowGid;
    }
    return [
      {
        tabId: 0,
        tabText: 'Document Details',
        tabRoute: {
          params,
          query,
          name: this.detailsRouteName,
        },
      },
      {
        tabId: 1,
        tabText: 'Associated With',
        tabRoute: {
          params,
          query,
          name: this.associationsRouteName,
        },
      },
    ];
  }

  // * COMPUTED
  private get orgId() {
    return this.$route.params.orgId;
  }
  private get projectGid() {
    return this.$route.params.projectGid;
  }
  private get contractGid() {
    return this.$route.params.contractGid;
  }
  private get workflowGid() {
    return this.$route.params.claimGid;
  }
  private get documentGid() {
    return this.$route.params.documentGid;
  }
  private get disableContextButton() {
    // if we have no effective organisation gid, we can't create new associations
    // Some of the ENG says to check org user project user contract user status, but that's not right nor needed
    return !this.effectiveOrganisationGid || !this.isMostRecentVersion;
  }
  /** This might be the number (version=3) or the version gid */
  private get urlVersion() {
    const version = this.$route.query.version;
    if (!version) {
      return null;
    }
    if (typeof version === 'string') {
      return version;
    }
    return version[0];
  }
  private get selectedVersion() {
    const lookingForVersion = this.urlVersion;
    // no document or no versions, not much we can do
    if (!this.document?.versions.length) {
      return null;
    }
    if (!lookingForVersion) {
      // first one. Last one. Dunno. First will do. ENG says "most recent" and also "sort by date desc"
      // so first one should be OK, if the list is sorted on the object
      return this.document.versions[0];
    }

    return (
      this.document.versions.find(x => x.index.toString() === lookingForVersion || areSame(x.gid, lookingForVersion)) ??
      null
    );
  }
  get isMostRecentVersion() {
    const maxVersionIndex = Math.max(...(this.document?.versions.map(v => v.index) || [0]));
    return this.selectedVersion?.index === maxVersionIndex;
  }

  private setSelectedVersion(version: DocumentVersion) {
    // TODO if this is the "default" version, remove the query param. Should also put the word "default" in the button text, maybe.
    this.$router.push({ query: { version: version.index.toString() } });
  }
  private get documentName() {
    return this.selectedVersion?.name || '';
  }

  private get routePath(): string | null {
    return this.$route.path || null;
  }
  private get currentRouteName() {
    return this.$route.name;
  }

  private get primaryContextBtn() {
    return this.newAssociationButton;
  }

  // * WATCHERS
  @Watch('routePath')
  private async checkRoutePath() {
    this.loading = true;

    if (!this.document || !areSame(this.document.gid, this.documentGid)) {
      await this.handleReloadDocument();
    }

    // this component is usable from a bunch of different routes, we might have a variety of differnt route params
    // so, check what we have, set the right breadcrumb info

    if (this.workflowGid && (!this.claimDetails || !areSame(this.claimDetails.gid, this.workflowGid))) {
      const result = await executeApi(
        () => this.api.segmentInstances().getSegmentInstanceDetails(this.workflowGid),
        'Load Workflow Details',
      );
      if (result.success && result.data) {
        // we can get everything we need from the claim/workflow details. It's CHOCKERS with data
        const claimDetails = SegmentInstanceDetails.createFromApiResponse(result.data);
        this.claimDetails = { gid: claimDetails.gid, name: claimDetails.name };
        this.projectDetails = { gid: claimDetails.project.gid, name: claimDetails.project.name };
        this.contractDetails = { gid: claimDetails.contract.gid, name: claimDetails.contract.name };
        this.effectiveOrganisationGid = claimDetails.project.organisationGid;

        vxm.breadcrumbs.setClaimName({ claimName: this.claimDetails.name });
        vxm.breadcrumbs.setProjectName({ projectName: this.projectDetails.name });
        vxm.breadcrumbs.setContractName({ contractName: this.contractDetails.name });
      }
    }

    if (this.contractGid && (!this.contractDetails || !areSame(this.contractDetails.gid, this.contractGid))) {
      const result = await executeApi(
        () => this.api.contracts().getContractDetails(this.contractGid),
        'Load Contract Details',
      );
      if (result.success && result.data && result.data.contract) {
        // contract details is a bit light on for anything other than the contract. Shame. We will have to load the project separately
        const contractDetails = ContractDetails.createFromApiResponse(result.data.contract);
        this.contractDetails = { gid: contractDetails.gid, name: contractDetails.name };
        this.effectiveOrganisationGid = contractDetails.organisationGid;
        vxm.breadcrumbs.setContractName({ contractName: this.contractDetails.name });
      }
    }
    if (this.projectGid && (!this.projectDetails || !areSame(this.projectDetails.gid, this.projectGid))) {
      const result = await executeApi(
        () => this.api.projects().getProjectDetails(this.projectGid),
        'Load Project Details',
      );
      if (result.success && result.data) {
        const projectDetails = ProjectDetails.createFromApiResponse(result.data);
        this.projectDetails = { gid: projectDetails.gid, name: projectDetails.name };
        this.effectiveOrganisationGid = projectDetails.organisationGid;
        vxm.breadcrumbs.setProjectName({ projectName: this.projectDetails.name });
      }
    }
    if (this.orgId && (!this.organisation || !areSame(this.organisation.gid, this.orgId))) {
      const result = await executeApi(
        () => this.api.organisation().getOrganisationDetails(this.orgId),
        'Load Organisation Details',
      );
      if (result.success && result.data) {
        const organisation = Organisation.createFromApiResponse(result.data);
        this.organisation = { gid: organisation.gid, name: organisation.name };
        this.effectiveOrganisationGid = organisation.gid;
        vxm.breadcrumbs.setOrganisationName({ organisation: this.organisation.name });
      }
    }

    this.loading = false;
  }
  @Watch('selectedVersion')
  private selectedVersionChanged() {
    console.log('selected version changed');
    if (this.selectedVersion) {
      vxm.breadcrumbs.setDocumentName({ documentName: this.selectedVersion.name });
    }
  }

  // * METHODS
  private formatDateAndTime(date: Date | null) {
    if (date) {
      return formatDateAndTime(date, { overrideJoin: '-' });
    }
    return '';
  }

  private async handleReloadDocument(callback?: () => void) {
    const result = await executeApi(() => this.api.documents().getDocument(this.documentGid), 'Load Document Details');
    if (result.success && result.data) {
      this.document = DocumentDetails.createFromApiResponse(result.data);
    }
    if (callback) {
      callback();
    }
  }

  private async createNewDocumentAssociation() {
    if (!this.effectiveOrganisationGid) {
      console.error('ot-document-layout: createNewDocumentAssociation: no effective organisation gid. Bailing');
      return;
    }
    const params = new CreateDocumentAssociationsDialogParams({
      targetOrganisationGid: this.effectiveOrganisationGid,
      documentGid: this.documentGid,
      addToOrganisation: false,
      addToProjectGids: [],
      addToContractGids: [],
    });

    const dialogResult = await vxm.modal.openCreateDocumentAssociationsDialog(params);
    if (dialogResult.ok) {
      this.handleReloadDocument();
    }
  }

  // * LIFECYCLE
  private created() {
    this.checkRoutePath();
  }
}
